<template>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-xl modal-dialog-scrollable" role="document">
      <div class="modal-content">
        <div class="modal-header text-white">
          <h5 class="modal-title ">
            <i class="bx bx-shield-plus me-2"></i>
            {{ isEditMode ? 'Edit CBHI Plan' : 'Add New CBHI Plan' }}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <!-- CBHI Pricing Rules Banner -->
          <div class="alert alert-info border-left-info mb-4">
            <h6 class="alert-heading"><i class="bx bx-info-circle me-2"></i>Ethiopian CBHI Pricing Rules</h6>
            <ul class="mb-0 small">
              <li><strong>Principal Member:</strong> Pays the annual premium amount you set</li>
              <li><strong>Immediate Family (Always FREE):</strong> Spouse + all children under 18 years</li>
              <li><strong>Additional Members:</strong> Automatically charged at <strong>20% (1/5)</strong> of principal amount</li>
              <li><strong>Lower Plan:</strong> Can be set as completely FREE for poverty-line families</li>
            </ul>
          </div>

          <form @submit.prevent="savePlan">
            <!-- Step 1: Plan Type Selection -->
            <div class="card mb-4">
              <div class="card-header bg-light">
                <h6 class="mb-0"><i class="bx bx-category me-2"></i>Step 1: Plan Type & Category</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <!-- Plan Type Selection -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Plan Category <span class="text-danger">*</span></label>
                    <div class="plan-type-selector">
                      <div class="form-check form-check-card mb-2" v-for="type in planTypes" :key="type.value">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="'planType' + type.value"
                            v-model="formData.planType"
                            :value="type.value"
                        >
                        <label class="form-check-label w-100" :for="'planType' + type.value">
                          <div class="d-flex align-items-center">
                            <i :class="type.icon + ' fs-4 me-3 ' + type.color"></i>
                            <div>
                              <strong>{{ type.label }}</strong>
                              <small class="text-muted d-block">{{ type.description }}</small>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Is this a FREE plan? -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Pricing Model <span class="text-danger">*</span></label>
                    <div class="plan-type-selector">
                      <div class="form-check form-check-card mb-2">
                        <input
                            class="form-check-input"
                            type="radio"
                            id="pricingFree"
                            v-model="formData.isFree"
                            :value="true"
                        >
                        <label class="form-check-label w-100" for="pricingFree">
                          <div class="d-flex align-items-center">
                            <i class="bx bx-heart fs-4 me-3 text-success"></i>
                            <div>
                              <strong class="text-success">FREE Plan (Lower)</strong>
                              <small class="text-muted d-block">For individuals below poverty line</small>
                              <small class="text-muted d-block fst-italic">Completely free - no charges for anyone</small>
                            </div>
                          </div>
                        </label>
                      </div>

                      <div class="form-check form-check-card">
                        <input
                            class="form-check-input"
                            type="radio"
                            id="pricingPaid"
                            v-model="formData.isFree"
                            :value="false"
                        >
                        <label class="form-check-label w-100" for="pricingPaid">
                          <div class="d-flex align-items-center">
                            <i class="bx bx-money fs-4 me-3 text-primary"></i>
                            <div>
                              <strong class="text-primary">PAID Plan</strong>
                              <small class="text-muted d-block">Regular premium-based coverage</small>
                              <small class="text-muted d-block fst-italic">Principal pays premium, immediate family FREE, extras 20%</small>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 2: Basic Information -->
            <div class="card mb-4">
              <div class="card-header bg-light">
                <h6 class="mb-0"><i class="bx bx-info-circle me-2"></i>Step 2: Basic Information</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Plan Name <span class="text-danger">*</span></label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="formData.planName"
                        placeholder="e.g., Lower Plan, Medium Plan, Upper Plan"
                        required
                    >
                  </div>

                  <div class="col-md-4 mb-3">
                    <label class="form-label">Plan Code <span class="text-danger">*</span></label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="formData.planCode"
                        placeholder="e.g., LOWER, MEDIUM, UPPER"
                        :readonly="isEditMode"
                        required
                    >
                    <small class="text-muted">Cannot be changed after creation</small>
                  </div>

                  <div class="col-md-4 mb-3">
                    <label class="form-label">Display Order</label>
                    <input
                        type="number"
                        class="form-control"
                        v-model.number="formData.displayOrder"
                        min="1"
                        placeholder="1"
                    >
                    <small class="text-muted">Lower numbers appear first</small>
                  </div>

                  <div class="col-12 mb-3">
                    <label class="form-label">Plan Description</label>
                    <textarea
                        class="form-control"
                        v-model="formData.planDescription"
                        rows="3"
                        placeholder="Describe this plan and its target audience..."
                    ></textarea>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Category Name</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="formData.categoryName"
                        placeholder="e.g., Free - Poverty Line, Standard, Premium"
                    >
                    <small class="text-muted">Display category for grouping</small>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Badge Color</label>
                    <select class="form-select" v-model="formData.badgeColor">
                      <option value="success">Green (Success)</option>
                      <option value="info">Blue (Info)</option>
                      <option value="warning">Yellow (Warning)</option>
                      <option value="danger">Red (Danger)</option>
                      <option value="primary">Primary Blue</option>
                      <option value="secondary">Gray (Secondary)</option>
                    </select>
                  </div>

                  <div class="col-12 mb-3">
                    <label class="form-label">Eligibility Text</label>
                    <textarea
                        class="form-control"
                        v-model="formData.eligibilityText"
                        rows="2"
                        :placeholder="formData.isFree ? 'e.g., Reserved for individuals below the poverty line, as identified by health extension workers' : 'e.g., Available to all eligible citizens'"
                    ></textarea>
                    <small class="text-muted">Who can enroll in this plan</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Pricing Configuration -->
            <div class="card mb-4">
              <div class="card-header bg-light">
                <h6 class="mb-0"><i class="bx bx-money me-2"></i>Step 3: Pricing Structure</h6>
              </div>
              <div class="card-body">
                <div v-if="formData.isFree" class="alert alert-success">
                  <i class="bx bx-check-circle me-2"></i>
                  <strong>FREE Plan:</strong> All charges are set to 0. No one pays anything for this plan.
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Annual Premium (Principal Member)
                      <span class="text-danger">*</span>
                    </label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input
                          type="number"
                          class="form-control"
                          v-model.number="formData.primaryAnnualAmount"
                          :readonly="formData.isFree"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          required
                      >
                    </div>
                    <small class="text-muted">
                      {{ formData.isFree ? 'Set to 0 for FREE plan' : 'Amount the principal member pays annually' }}
                    </small>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Additional Member Rate
                      <span class="badge bg-info">Auto-calculated</span>
                    </label>
                    <div class="input-group">
                      <input
                          type="number"
                          class="form-control"
                          :value="formData.isFree ? 0 : (formData.additionalMemberRate * 100)"
                          readonly
                          step="0.01"
                      >
                      <span class="input-group-text">%</span>
                    </div>
                    <small class="text-muted">
                      {{ formData.isFree ? 'Free for all family members' : 'Fixed at 20% (1/5 of principal) per CBHI rules' }}
                    </small>
                  </div>

                  <div class="col-12 mb-3">
                    <div class="card bg-light">
                      <div class="card-body">
                        <h6 class="card-title">Pricing Breakdown Preview</h6>
                        <div class="row text-center">
                          <div class="col-md-4">
                            <div class="pricing-item">
                              <i class="bx bx-user text-primary fs-3"></i>
                              <h5 class="mt-2">ETB {{ formatCurrency(formData.primaryAnnualAmount) }}</h5>
                              <p class="text-muted small mb-0">Principal Member</p>
                            </div>
                          </div>
                          <div class="col-md-4">
                            <div class="pricing-item">
                              <i class="bx bx-group text-success fs-3"></i>
                              <h5 class="mt-2 text-success">FREE</h5>
                              <p class="text-muted small mb-0">Immediate Family<br>(Spouse + Children &lt;18)</p>
                            </div>
                          </div>
                          <div class="col-md-4">
                            <div class="pricing-item">
                              <i class="bx bx-user-plus text-info fs-3"></i>
                              <h5 class="mt-2">ETB {{ formatCurrency(additionalMemberCost) }}</h5>
                              <p class="text-muted small mb-0">Each Additional Member<br>(Adults 18+, Relatives)</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 4: Dates -->
            <div class="card mb-4">
              <div class="card-header bg-light">
                <h6 class="mb-0"><i class="bx bx-calendar me-2"></i>Step 4: Effective Dates</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Effective From <span class="text-danger">*</span></label>
                    <input
                        type="date"
                        class="form-control"
                        v-model="formData.effectiveFrom"
                        required
                    >
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Effective To <span class="text-muted">(Optional)</span></label>
                    <input
                        type="date"
                        class="form-control"
                        v-model="formData.effectiveTo"
                    >
                    <small class="text-muted">Leave empty for no expiry</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 5: Status -->
            <div class="card mb-4">
              <div class="card-header bg-light">
                <h6 class="mb-0"><i class="bx bx-toggle-right me-2"></i>Step 5: Plan Status</h6>
              </div>
              <div class="card-body">
                <div class="form-check form-switch">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      id="isActive"
                      v-model="formData.isActive"
                  >
                  <label class="form-check-label" for="isActive">
                    <strong>{{ formData.isActive ? 'Active' : 'Inactive' }}</strong>
                    <small class="text-muted d-block">
                      {{ formData.isActive ? 'Plan is available for enrollment' : 'Plan is hidden from enrollment' }}
                    </small>
                  </label>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div class="modal-footer border-top">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            <i class="bx bx-x me-1"></i>Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="savePlan" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bx bx-check me-1"></i>
            {{ isEditMode ? 'Update Plan' : 'Create Plan' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {authUrl, executeJson} from "@/api";

export default {
  props: {
    plan: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      formData: {
        planName: '',
        planCode: '',
        planDescription: '',
        planType: 'basic',
        categoryName: '',
        badgeColor: 'info',
        eligibilityText: '',
        primaryAnnualAmount: 0,
        additionalMemberRate: 0.20, // Fixed at 20% (1/5) per CBHI rules
        displayOrder: 1,
        effectiveFrom: new Date().toISOString().split('T')[0],
        effectiveTo: '',
        isActive: true,
        isFree: false
      },
      saving: false,
      planTypes: [
        {
          value: 'basic',
          label: 'Basic',
          icon: 'bx bx-shield',
          color: 'text-secondary',
          description: 'Essential coverage'
        },
        {
          value: 'standard',
          label: 'Standard',
          icon: 'bx bx-shield-check',
          color: 'text-primary',
          description: 'Standard coverage level'
        },
        {
          value: 'premium',
          label: 'Premium',
          icon: 'bx bx-shield-plus',
          color: 'text-success',
          description: 'Enhanced coverage'
        },
        {
          value: 'family',
          label: 'Family',
          icon: 'bx bx-group',
          color: 'text-info',
          description: 'Family-focused plan'
        }
      ]
    };
  },

  computed: {
    isEditMode() {
      return this.plan !== null;
    },

    additionalMemberCost() {
      if (this.formData.isFree) return 0;
      return this.formData.primaryAnnualAmount * this.formData.additionalMemberRate;
    }
  },

  watch: {
    'formData.isFree'(newValue) {
      if (newValue) {
        // Set to FREE
        this.formData.primaryAnnualAmount = 0;
        this.formData.additionalMemberRate = 0;
        if (!this.formData.categoryName) {
          this.formData.categoryName = 'Free - Poverty Line';
        }
        if (!this.formData.eligibilityText) {
          this.formData.eligibilityText = 'Reserved for individuals below the poverty line, as identified by health extension workers';
        }
        this.formData.badgeColor = 'success';
      } else {
        // Set to PAID
        this.formData.additionalMemberRate = 0.20; // Reset to 20%
        if (this.formData.categoryName === 'Free - Poverty Line') {
          this.formData.categoryName = '';
        }
        if (this.formData.eligibilityText.includes('poverty line')) {
          this.formData.eligibilityText = 'Available to all eligible citizens';
        }
      }
    }
  },

  mounted() {
    if (this.isEditMode) {
      this.loadPlanData();
    }
  },

  methods: {
    loadPlanData() {
      this.formData = {
        planName: this.plan.plan_name || '',
        planCode: this.plan.plan_code || '',
        planDescription: this.plan.plan_description || '',
        planType: this.plan.plan_type || 'basic',
        categoryName: this.plan.category_name || '',
        badgeColor: this.plan.badge_color || 'info',
        eligibilityText: this.plan.eligibility_text || '',
        primaryAnnualAmount: parseFloat(this.plan.primary_annual_amount) || 0,
        additionalMemberRate: parseFloat(this.plan.additional_member_rate) || 0.20,
        displayOrder: parseInt(this.plan.display_order) || 1,
        effectiveFrom: this.plan.effective_from || new Date().toISOString().split('T')[0],
        effectiveTo: this.plan.effective_to || '',
        isActive: this.plan.is_active === '1' || this.plan.is_active === 1 || this.plan.is_active === true,
        isFree: parseFloat(this.plan.primary_annual_amount) === 0
      };
    },

    async savePlan() {
      // Validation
      if (!this.formData.planName || !this.formData.planCode) {
        this.$emit('error', {message: 'Plan name and code are required'});
        return;
      }

      this.saving = true;

      const data = {
        function: this.isEditMode ? 'updateCBHIPlan' : 'addCBHIPlan',
        planName: this.formData.planName,
        planCode: this.formData.planCode,
        planDescription: this.formData.planDescription,
        planType: this.formData.planType,
        categoryName: this.formData.categoryName,
        badgeColor: this.formData.badgeColor,
        eligibilityText: this.formData.eligibilityText,
        primaryAnnualAmount: this.formData.isFree ? 0 : this.formData.primaryAnnualAmount,
        additionalMemberRate: this.formData.isFree ? 0 : this.formData.additionalMemberRate,
        displayOrder: this.formData.displayOrder,
        effectiveFrom: this.formData.effectiveFrom,
        effectiveTo: this.formData.effectiveTo || null,
        isActive: this.formData.isActive
      };

      if (this.isEditMode) {
        data.planId = this.plan.plan_id;
      }

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.$emit('saved', {
            message: this.isEditMode ? 'Plan updated successfully' : 'Plan created successfully'
          });
          this.closeModal();
        } else {
          this.$emit('error', {message: res.data.message || 'Failed to save plan'});
        }
      } catch (e) {
        this.$emit('error', {message: e.message || 'Failed to save plan'});
      } finally {
        this.saving = false;
      }
    },

    closeModal() {
      this.$emit('close');
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    }
  }
};
</script>

<style scoped>
.modal.show {
  display: block;
}

.form-check-card {
  border: 2px solid #e9ecef;
  border-radius: 0.375rem;
  padding: 1rem;
  transition: all 0.2s;
  cursor: pointer;
}

.form-check-card:hover {
  border-color: #0d6efd;
  background-color: #f8f9fa;
}

.form-check-card input:checked ~ label {
  border-color: #0d6efd;
  background-color: #e7f1ff;
}

/* Better spacing for radio inputs */
.form-check-card .form-check-input {
  margin-top: 0.25rem;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

/* Ensure the label takes full width */
.form-check-card .form-check-label {
  margin-left: 0;
  flex: 1;
}

/* Add spacing between icon and text */
.form-check-card .form-check-label i {
  flex-shrink: 0;
}

/* Ensure proper alignment */
.form-check-card .d-flex {
  align-items: flex-start;
}

.border-left-info {
  border-left: 4px solid #17a2b8;
}

.pricing-item {
  padding: 1rem;
  border-right: 1px solid #dee2e6;
}

.pricing-item:last-child {
  border-right: none;
}

.plan-type-selector .form-check-card {
  cursor: pointer;
  margin-bottom: 0.75rem;
}

/* Remove default margin on last card */
.plan-type-selector .form-check-card:last-child {
  margin-bottom: 0;
}
</style>